/**
 * Shared hour / count formatters for timekeeping displays.
 */

/**
 * Rounds decimal hours to payroll precision (2 decimal places).
 * Worker hours are stored this way; payout values must match.
 *
 * @param {number|string|null|undefined} value - Decimal hours.
 * @returns {number} Hours rounded to 2 decimal places.
 */
export function roundToPayrollHours(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * Returns whether two hour values differ at payroll precision.
 *
 * @param {number|string|null|undefined} left - First hour value.
 * @param {number|string|null|undefined} right - Second hour value.
 * @returns {boolean} True when the rounded values differ.
 */
export function payrollHoursDiffer(left, right) {
  return roundToPayrollHours(left) !== roundToPayrollHours(right);
}

/**
 * Formats decimal hours for compact dashboard display (dash when empty).
 *
 * @param {number|null|undefined} value - Decimal hour value.
 * @returns {string} Formatted hour string.
 */
export function formatHours(value) {
  if (value === null || value === undefined || Number(value) === 0) {
    return "-";
  }
  return Number(value).toFixed(2);
}

/**
 * Formats hour deltas with a plus sign for positive values.
 *
 * @param {number|null|undefined} value - Decimal hour delta.
 * @returns {string} Formatted delta string.
 */
export function formatHoursDelta(value) {
  const formatted = formatHours(value);
  if (formatted === "-") {
    return formatted;
  }
  return Number(value) > 0 ? `+${formatted}` : formatted;
}

/**
 * Formats whole-number counts with a dash for empty values.
 *
 * @param {number|null|undefined} value - Count value.
 * @returns {string|number} Formatted count display.
 */
export function formatHoursCount(value) {
  if (value === null || value === undefined || Number(value) === 0) {
    return "-";
  }
  return value;
}

/** @deprecated Prefer {@link formatHours}. Kept for payout dashboard call sites. */
export const formatDashboardHours = formatHours;

/** @deprecated Prefer {@link formatHoursDelta}. */
export const formatDashboardDelta = formatHoursDelta;

/** @deprecated Prefer {@link formatHoursCount}. */
export const formatDashboardCount = formatHoursCount;
